import React, { useState } from 'react';
import { usePaymentTransactions } from '@/hooks/usePaymentTransactions';
import { formatCurrency, formatDate } from '@/utils/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, RefreshCw, Search, Filter, X } from 'lucide-react';

export const PaymentTransactions = ({ API }) => {
  const {
    transactions,
    loading,
    filters,
    updateFilters,
    clearFilters,
    fetchTransactions,
    exportToCSV
  } = usePaymentTransactions(API);

  const [showFilters, setShowFilters] = useState(false);

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      paid: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
      open: 'bg-blue-100 text-blue-800 border-blue-200',
      unpaid: 'bg-orange-100 text-orange-800 border-orange-200'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusStyles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {status?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const getGatewayBadge = (gateway) => {
    const gatewayStyles = {
      nexi: 'bg-orange-50 text-orange-700 border-orange-200',
      stripe: 'bg-blue-50 text-blue-700 border-blue-200',
      paypal: 'bg-indigo-50 text-indigo-700 border-indigo-200'
    };

    const gatewayIcons = {
      nexi: '🟧',
      stripe: '💳',
      paypal: '🅿️'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border inline-flex items-center gap-1 ${gatewayStyles[gateway] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
        <span>{gatewayIcons[gateway] || '💰'}</span>
        {gateway?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const getPaymentTypeBadge = (type) => {
    const typeStyles = {
      deposit: 'bg-purple-50 text-purple-700',
      full_payment: 'bg-indigo-50 text-indigo-700'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs ${typeStyles[type] || 'bg-gray-50 text-gray-700'}`}>
        {type === 'deposit' ? 'Deposit' : type === 'full_payment' ? 'Full Payment' : type}
      </span>
    );
  };

  const hasActiveFilters = filters.payment_gateway || filters.status || filters.search || filters.date_from || filters.date_to;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payment Transactions</h2>
          <p className="text-sm text-gray-600 mt-1">
            View and manage all payment transactions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                {[filters.payment_gateway, filters.status, filters.search, filters.date_from, filters.date_to].filter(Boolean).length}
              </span>
            )}
          </Button>
          <Button
            onClick={fetchTransactions}
            disabled={loading}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={exportToCSV}
            disabled={transactions.length === 0}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Filters</h3>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label htmlFor="search" className="text-sm font-medium">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="search"
                  type="text"
                  placeholder="Booking ID, email..."
                  value={filters.search}
                  onChange={(e) => updateFilters({ search: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Payment Gateway */}
            <div className="space-y-2">
              <Label htmlFor="gateway" className="text-sm font-medium">
                Payment Gateway
              </Label>
              <select
                id="gateway"
                value={filters.payment_gateway}
                onChange={(e) => updateFilters({ payment_gateway: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Gateways</option>
                <option value="nexi">Nexi XPay</option>
                <option value="stripe">Stripe</option>
              </select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status" className="text-sm font-medium">
                Status
              </Label>
              <select
                id="status"
                value={filters.status}
                onChange={(e) => updateFilters({ status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
                <option value="open">Open</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            {/* Limit */}
            <div className="space-y-2">
              <Label htmlFor="limit" className="text-sm font-medium">
                Records Limit
              </Label>
              <select
                id="limit"
                value={filters.limit}
                onChange={(e) => updateFilters({ limit: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <Label htmlFor="date_from" className="text-sm font-medium">
                Date From
              </Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from}
                onChange={(e) => updateFilters({ date_from: e.target.value })}
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label htmlFor="date_to" className="text-sm font-medium">
                Date To
              </Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to}
                onChange={(e) => updateFilters({ date_to: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-600">Total Transactions</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{transactions.length}</div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-600">Paid</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {transactions.filter(t => t.status === 'paid' || t.status === 'completed').length}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-600">Pending</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">
            {transactions.filter(t => t.status === 'pending').length}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-600">Total Amount</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {formatCurrency(transactions.reduce((sum, t) => sum + (t.amount || 0), 0))}
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading transactions...</span>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-medium">No transactions found</p>
            <p className="text-sm mt-1">Try adjusting your filters or create a new booking</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Booking ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Guest</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Gateway</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Order ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.created_at)}
                      <div className="text-xs text-gray-500">
                        {new Date(transaction.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium text-gray-900">{transaction.booking_id}</div>
                      {transaction.booking_info && (
                        <div className="text-xs text-gray-500">
                          {transaction.booking_info.check_in} → {transaction.booking_info.check_out}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {transaction.booking_info ? (
                        <div>
                          <div className="font-medium text-gray-900">{transaction.booking_info.guest_name}</div>
                          <div className="text-xs text-gray-500">{transaction.booking_info.guest_email}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getGatewayBadge(transaction.payment_gateway || transaction.gateway)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getPaymentTypeBadge(transaction.payment_type)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(transaction.amount)}
                      <div className="text-xs text-gray-500 font-normal">{transaction.currency?.toUpperCase()}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(transaction.status || transaction.payment_status)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 font-mono max-w-xs truncate" title={transaction.order_id}>
                      {transaction.order_id || transaction.session_id || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      {transactions.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            Showing <span className="font-medium text-gray-900">{transactions.length}</span> transactions
          </div>
          <div>
            Last updated: {new Date().toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
};
